import {
    calculatePlatformFee,
    getCocktailPricePerItemCents,
    getCocktailProduct,
    getCocktailProducts,
} from '@/helpers/quoteFunctions';
import { convertCentsToDollars } from '@thdinc/hpf-shared';
import React, { FC } from 'react';

type CocktailItemLine = {
    id: string;
    provider?: any | '';
    type?: any | '';
    cocktails?: any | '';
    brand?: any | '';
    brandName?: string | '';
    qty?: number | string | '';
    pricePerItem?: number | string | '';
};

interface QuoteForm {
    items: CocktailItemLine[];
    subTotal: number;
    numberOfCocktail: number | '';
    platformFee: number;
    stateTaxFee: number;
}
/* ---------- Mock Data (replace with real data) ---------- */
type Item = {
    name: string;
    description?: string;
    qty?: number | null; // null/undefined renders "-" like in screenshot
    price?: number | null; // per-item price
    amount?: number | null; // optional override (e.g., “Service total”)
};

type ItemRowProps = {
    item: any;
};

/* ---------- Helpers ---------- */
const fmt = (n: number) =>
    n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    });

const resolveAmount = (it: Item) =>
    typeof it.amount === 'number'
        ? it.amount
        : typeof it.qty === 'number' && typeof it.price === 'number'
            ? it.qty * it.price
            : 0;

/* ---------- Component ---------- */
const CocktailPreview: FC<QuoteForm> = ({ items, subTotal, numberOfCocktail, platformFee = 0, stateTaxFee = 0 }) => {
    const subtotal = subTotal;
    const total = subtotal > 0 ? subtotal + calculatePlatformFee(subtotal, platformFee) + calculatePlatformFee(subtotal, stateTaxFee) : 0;

    return (
        <div className='w-full bg-white'>
            {/* Title */}
            <h1 className='font-[600] text-[16px] text-[#282828]'>Quote</h1>
            {/* Section header */}
            <h2 className='mt-4 text text-[#202224] font-[600] text-[12.62px]'>
                Alcohol Details
            </h2>
            <p className='text-[#8D8D8D] text-xs mb-3'>
                This is the alcohol that we will drop off. Not necessarily the alcohol that will be
                charged.
            </p>
            {/* Table */}
            <div className='overflow-x-auto'>
                {/* Header */}
                <div className='grid grid-cols-6 font-[500] text-xs text-[#202224] border-b border-gray-200 border-t'>
                    <div className='px-2 py-2 text-left text-[10px]'>Item</div>
                    <div className='px-2 py-2 text-center text-[10px]'>Cocktail</div>
                    <div className='px-2 py-2 text-center text-[10px]'>Description</div>
                    <div className='px-2 py-2 text-center text-[10px]'>Qty</div>
                    <div className='px-2 py-2 text-center text-[10px]'>Price per item</div>
                    <div className='px-4 py-3 text-right text-[10px]'>Amount</div>
                </div>
                {/* Rows */}
                <div className='divide-gray-100'>
                    {items?.map((it, idx) => {
                        return (
                            <div key={idx}>
                                {it?.provider?.value !== 'customer-provide' && it?.provider?.value !== 'ho-provide' && (
                                    <>
                                        {(() => {
                                            const item = {
                                                item: it?.type?.name || '',
                                                cocktail: it?.cocktails?.name || '',
                                                desc:
                                                    it?.type?.value == 'other'
                                                        ? it?.brandName
                                                        : it?.brand?.name,
                                                quantity: it?.qty ?? '-',
                                                pricePreItem:
                                                    it?.type?.value == 'other'
                                                        ? '0.00'
                                                        : it?.brand?.id == 'other'
                                                            ? '0.00'
                                                            : getCocktailPricePerItemCents(it) ?? '-',
                                                amount: Number(it?.qty) * getCocktailPricePerItemCents(it),
                                            };

                                            return <ItemRow key={`${idx}`} item={item} />;
                                        })()}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Summary */}
            <div className='mt-6 grid grid-cols-2 gap-4  w-full'>
                <div className='w-full'>{/* LEFT COLUMN */}</div>
                <div className='text-sm w-full'>
                    <Row label='Subtotal' value={fmt(subtotal)} />
                    {/* <Row
                        label='HOT Platform Fee'
                        value={fmt(calculatePlatformFee(subtotal, platformFee))}
                    /> */}
                    <Row
                        label='Sales Tax'
                        value={fmt(calculatePlatformFee(subtotal, stateTaxFee))}
                    />
                    <p className="text-[9px] text-[#8D8D8D] font-[400] ">
                        (Alcohol sales tax (8.75%–11%) applies when alcohol is charged)
                    </p>
                    <div className='mt-3 flex items-center justify-between border-t'>
                        <span className='text-sm font-medium text-gray-700'>Total</span>
                        <span className='text-lg font-semibold text-gray-900'>{fmt(total)}</span>
                    </div>
                </div>
            </div>

            {/* Customer Provides - only when there is client-provided data */}
            {items?.some((it) => it?.provider?.value === 'customer-provide') && (
                <div className='mt-6 rounded-2xl bg-gray-50 p-4'>
                    <p className='text-sm text-gray-800 mb-3 text-[10px] font-[600]'>
                        Client Provides
                    </p>
                    <div className='flex flex-wrap gap-2'>
                        {items?.map((it, idx) => {
                            return (
                                <div key={idx}>
                                    {it?.provider?.value == 'customer-provide' && (
                                        <>
                                            {(() => {
                                                const item = {
                                                    item: it?.type?.name || '',
                                                    cocktail: it?.cocktails?.name || '',
                                                    desc:
                                                        it?.type?.value == 'other' || it?.cocktails?.value == "custom"
                                                            ? (!it?.brandName ? it?.brand?.name : it?.brandName)
                                                            : it?.brand?.name,
                                                    quantity: it?.qty,
                                                    pricePreItem: it?.brand?.price ?? '-',
                                                    amount: Number(it?.qty) * Number(it?.brand?.price),
                                                };
                                                return <CustomerProvide key={`${idx}`} item={item} />;
                                            })()}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* Selling Cocktails - only when there is HO-provided data */}
            {items?.some((it) => it?.provider?.value === 'ho-provide') && (
                <div className='mt-6 rounded-2xl bg-gray-50 p-4'>
                    <p className='text-sm text-gray-800 mb-3 text-[10px] font-[600]'>
                        Selling Cocktails
                    </p>
                    <div className='flex flex-wrap gap-2'>
                        {items?.map((it, idx) => {
                            return (
                                <div key={idx}>
                                    {it?.provider?.value == 'ho-provide' && (
                                        <>
                                            {(() => {
                                                const item = {
                                                    item: it?.type?.name || '',
                                                    cocktail: it?.cocktails?.name || '',
                                                    desc:
                                                        it?.type?.value == 'other' || it?.cocktails?.value == "custom"
                                                            ? (!it?.brandName ? it?.brand?.name : it?.brandName)
                                                            : it?.brand?.name,
                                                    quantity: it?.qty,
                                                    pricePreItem: it?.brand?.price ?? '-',
                                                    amount: Number(it?.qty) * Number(it?.brand?.price),
                                                };
                                                return <CustomerProvide key={`${idx}`} item={item} />;
                                            })()}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CocktailPreview;

const ItemRow: FC<ItemRowProps> = ({ item }: any) => {
    return (
        <div className='grid grid-cols-6 text-sm border-b'>
            <div className='px-4 py-3 text-gray-800 text-[10px] leading-[1.3]'>{item?.item}</div>
            <div className='px-4 py-3 text-gray-800 text-[10px] leading-[1.3]'>
                {item?.cocktail}
            </div>
            <div className='px-4 py-3 text-gray-800 text-[10px] leading-[1.3]'>{item?.desc}</div>
            <div className='px-4 py-3 text-gray-800 text-[10px] leading-[1.3]'>
                {isNaN(Number(item.quantity)) ? '-' : Number(item.quantity)}
            </div>
            <div className='px-4 py-3 text-gray-800 text-[10px] leading-[1.3]'>
                ${convertCentsToDollars(Number.isFinite(item.pricePreItem) ? item.pricePreItem : 0)}
            </div>
            <div className='px-4 py-3 text-gray-800 text-[10px] leading-[1.3]'>
                {isNaN(Number(convertCentsToDollars(item.amount)))
                    ? '-'
                    : `$${convertCentsToDollars(item.amount)}`}
            </div>
        </div>
    );
};
const CustomerProvide: FC<ItemRowProps> = ({ item }: any) => {
    return (
        <span className='inline-flex items-center gap-3 rounded-full bg-white px-3 py-1.5 text-sm'>
            <span className='text-[10px]'>{item.desc}:</span>
            <span className='text-[10px] inline-flex  items-center px-2 text-[#8D8D8D]'>
                {isNaN(Number(item.quantity)) ? '-' : item.quantity}
            </span>
        </span>
    );
};

/* ---------- Small subcomponent ---------- */
function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className='flex items-center justify-between text-[12px] font-[400]'>
            <span className='text-gray-600'>{label}</span>
            <span className='text-gray-800'>{value}</span>
        </div>
    );
}
